export type GiftPassScope = 'SINGLE' | 'DUAL';

export const MEMBER_INVITE_BLOCK_MESSAGE =
  'This person is already a member. Use Transfer to Member.';

export const LA_PASS_GIFTCARD_KEY = 'laPassGiftcard';
export const QUICK_LINKS_HOLD_KEY = 'qc_ql_hold_key';
export const QUICK_LINKS_HOLD_REQUEST_KEY = 'qc_ql_hold_request';

export type GiftLandingContext = {
  token: string;
  holdKey: string;
  requestId: string;
};

export function applyGiftLanding(
  current: GiftLandingContext,
  giftcard: string | null,
): GiftLandingContext & { clearInvite: boolean } {
  if (giftcard == null) return { ...current, clearInvite: false };
  if (!giftcard) {
    return {
      token: '', holdKey: '', requestId: '', clearInvite: true,
    };
  }
  if (giftcard === current.token) return { ...current, clearInvite: false };
  return {
    token: giftcard, holdKey: '', requestId: '', clearInvite: false,
  };
}

const SIGNUP_ENDING =
  "Dive in and explore the site and when you're ready, sign up and get started!";

export function giftCountWithinInventory(count: number, available: number): boolean {
  return Number.isInteger(count) && count >= 1 && count <= available;
}

export type GiftInvitePreview = {
  scope: GiftPassScope;
  quantity: number;
  contactName?: string;
  message?: string;
};

export function inviteeFirstName(contactName?: string): string {
  return String(contactName || '').trim().split(/\s+/)[0] || '';
}

export function giftInviteWelcome(
  input: GiftInvitePreview,
): { beforeName: string; afterName: string } {
  const firstName = inviteeFirstName(input.contactName);
  const nameLead = firstName
    ? `Hello ${firstName}! Your inviter, `
    : 'Hello! Your inviter, ';
  const count = Number.isInteger(input.quantity) && input.quantity > 0 ? input.quantity : 1;
  const platform = input.scope === 'DUAL'
    ? 'both iPhone and Android'
    : 'either iPhone or Android';
  const who = count === 1
    ? 'you'
    : `you and ${count - 1} more of your family, friends and contacts`;
  return {
    beforeName: nameLead,
    afterName: `, has reserved a Lifetime Access Pass gift card for ${who}, redeemable for ${platform}. ${SIGNUP_ENDING}`,
  };
}

const redeeming = new Map<string, Promise<void>>();

export async function redeemStoredGiftcard(
  post: (token: string) => Promise<unknown>,
): Promise<void> {
  if (typeof localStorage === 'undefined') return;
  const token = localStorage.getItem(LA_PASS_GIFTCARD_KEY);
  if (!token) return;
  const existing = redeeming.get(token);
  if (existing) {
    await existing;
    return;
  }
  const pending = (async () => {
    try {
      await post(token);
      localStorage.removeItem(LA_PASS_GIFTCARD_KEY);
    } finally {
      redeeming.delete(token);
    }
  })();
  redeeming.set(token, pending);
  await pending;
}

export type GiftSignupOrigin = 'web' | 'app';

export function giftSignupOrigin(): GiftSignupOrigin {
  if (typeof window === 'undefined') return 'web';
  const params = new URLSearchParams(window.location.search);
  const redirect = params.get('redirect_uri')
    || window.localStorage.getItem('redirect_uri')
    || '';
  return redirect ? 'app' : 'web';
}

export function giftOnboardingStepCopy(origin: GiftSignupOrigin): string {
  const destination = origin === 'app'
    ? 'when you return to the app'
    : 'when you open the web dashboard';
  return `Your Lifetime Access gift has been accepted into onboarding. The pass activates ${destination}.`;
}

export async function resumeStoredCampaign(input: {
  read: (token: string) => Promise<{ kind?: string } | null | undefined>;
  join: (token: string) => Promise<unknown>;
  bind?: (token: string, holdKey: string) => Promise<{ awarded?: boolean; bound?: boolean } | void>;
}): Promise<boolean> {
  const token = localStorage.getItem(LA_PASS_GIFTCARD_KEY);
  if (!token) return false;
  const holdKey = localStorage.getItem(QUICK_LINKS_HOLD_KEY);
  try {
    if (holdKey && input.bind) {
      const bound = await input.bind(token, holdKey);
      if (bound && bound.awarded) {
        localStorage.removeItem(QUICK_LINKS_HOLD_KEY);
        localStorage.removeItem(QUICK_LINKS_HOLD_REQUEST_KEY);
      }
      if (bound && bound.bound === false) return false;
    }
    const preview = await input.read(token);
    if (preview?.kind !== 'campaign') return false;
    await input.join(token);
    return true;
  } catch {
    return false;
  }
}

export async function claimStoredGiftSignup(
  post: (token: string, origin: GiftSignupOrigin) => Promise<{ ok?: boolean } | void>,
): Promise<void> {
  return redeemStoredGiftcard(async (token) => {
    const result = await post(token, giftSignupOrigin());
    if (result && result.ok === false) {
      throw new Error('Gift claim was not saved.');
    }
  });
}
